import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BookOpenText, Wand2, Copy, Download, EyeOff, Eye } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

const MODELS = [
  { value: "claude-sonnet-4-6", label: "Claude Sonnet 4.6 (Anthropic)" },
  { value: "gpt-5.6-sol", label: "GPT-5.6 Sol (OpenAI, le + puissant)" },
  { value: "gpt-5.6-terra", label: "GPT-5.6 Terra (OpenAI, équilibré)" },
  { value: "gpt-5.6-luna", label: "GPT-5.6 Luna (OpenAI, rapide)" },
  { value: "gpt-5.5", label: "GPT-5.5 (OpenAI)" },
  { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro (Vertex AI, UE)" },
];

export const ClinicalSynthesis = ({ patientId }) => {
  const [synthesis, setSynthesis] = useState(null);
  const [model, setModel] = useState("claude-sonnet-4-6");
  const [instructions, setInstructions] = useState("");
  const [version, setVersion] = useState("nominative");
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api
      .get(`/patients/${patientId}/synthesis`)
      .then((res) => {
        if (res.data && res.data.content) setSynthesis(res.data);
      })
      .catch(() => {});
  }, [patientId]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/patients/${patientId}/synthesis`, { model, instructions });
      setSynthesis(res.data);
      toast.success("Synthèse clinique générée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de génération de la synthèse");
    } finally {
      setGenerating(false);
    }
  };

  const content = synthesis
    ? version === "anonyme"
      ? synthesis.content_anonymous
      : synthesis.content
    : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success("Synthèse copiée");
    } catch (e) {
      toast.error("Impossible de copier");
    }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/patients/${patientId}/synthesis/docx`, {
        params: { version },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `synthese-clinique-${version === "anonyme" ? "anonymisee" : "nominative"}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Erreur de téléchargement");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="mt-10" data-testid="clinical-synthesis-section">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="font-heading text-xl font-medium text-[#2D2C28] flex items-center gap-2">
          <BookOpenText className="w-5 h-5 text-[#C07761]" strokeWidth={1.5} /> Synthèse clinique
        </h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger data-testid="synthesis-model-select" className="bg-white rounded-xl sm:w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MODELS.map((m) => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={generate}
            disabled={generating}
            data-testid="generate-synthesis-btn"
            className="rounded-xl bg-[#C07761] hover:bg-[#A66350] text-white"
          >
            <Wand2 className="w-4 h-4 mr-2" />
            {generating ? "Génération (1-2 min)…" : synthesis ? "Régénérer" : "Générer la synthèse"}
          </Button>
        </div>
      </div>

      <div className="mb-4">
        <Label className="text-xs text-[#757169]">Instructions spécifiques (optionnel)</Label>
        <Textarea
          data-testid="synthesis-instructions-input"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder="Ex. insister sur les résultats des exercices d'exposition, détailler la procédure EMDR appliquée…"
          className="mt-1.5 bg-white rounded-xl min-h-[60px]"
        />
      </div>

      {!synthesis ? (
        <div className="bg-white rounded-2xl border border-dashed border-[#EAE6DF] p-8 text-center">
          <p className="text-[#757169] text-sm max-w-md mx-auto">
            Générez une synthèse consolidée de toutes les séances transcrites : motif de consultation,
            déroulé chronologique, techniques appliquées, évolution et résultats. Idéal pour rédiger des
            cas cliniques (version anonymisée incluse).
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#EAE6DF] shadow-[0_4px_20px_rgba(45,44,40,0.04)]">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 pt-5">
            <div className="flex items-center gap-1 bg-[#FBF9F6] border border-[#EAE6DF] rounded-full p-1">
              <button
                onClick={() => setVersion("nominative")}
                data-testid="synthesis-tab-nominative"
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  version === "nominative" ? "bg-[#C07761] text-white" : "text-[#757169]"
                }`}
              >
                <Eye className="w-3.5 h-3.5" /> Nominative
              </button>
              <button
                onClick={() => setVersion("anonyme")}
                data-testid="synthesis-tab-anonyme"
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  version === "anonyme" ? "bg-[#7B968B] text-white" : "text-[#757169]"
                }`}
              >
                <EyeOff className="w-3.5 h-3.5" /> Anonymisée
              </button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={copy}
                data-testid="copy-synthesis-btn"
                className="rounded-lg border-[#EAE6DF] text-[#575349] h-8 text-xs"
              >
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copier
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={download}
                disabled={downloading}
                data-testid="download-synthesis-btn"
                className="rounded-lg border-[#EAE6DF] text-[#575349] h-8 text-xs"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" />
                {downloading ? "…" : "Word (.docx)"}
              </Button>
            </div>
          </div>

          <div
            data-testid="synthesis-content"
            className="synthesis-md px-6 py-5 text-sm text-[#575349] leading-relaxed max-h-[520px] overflow-y-auto"
          >
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>

          <div className="px-6 pb-4">
            <Label className="text-xs text-[#9c988f]">
              Générée le {format(parseISO(synthesis.generated_at), "d MMMM yyyy 'à' HH:mm", { locale: fr })} ·{" "}
              {synthesis.sessions_count} séance(s) · {synthesis.model_used}
              {version === "anonyme" && " · Version anonymisée : relisez avant publication."}
            </Label>
          </div>
        </div>
      )}
    </div>
  );
};
